import type { EditorModel } from "@client/lib/scenarioDraft";

/**
 * The recovery buffer described in docs/08-frontend.md#local-resilience.
 *
 * This is **not** storage. The server is the source of truth; this exists so a
 * closed laptop, a crashed tab or a save attempted on a train does not cost
 * someone an afternoon of typing. It is written on every change, offered on the
 * next load, and deleted the moment a save succeeds.
 *
 * Every access is wrapped: `localStorage` throws in a private window, when site
 * data is blocked, and when the quota is full, and none of those are worth
 * taking the editor down for.
 */

const PREFIX = "roi:draft:";

export interface DraftMirror {
  /** The version the mirrored draft was edited from. */
  version: number;
  /** ISO 8601, so the offer can say how old the recovered work is. */
  savedAt: string;
  model: EditorModel;
}

const keyFor = (scenarioId: string): string => `${PREFIX}${scenarioId}`;

export function readDraftMirror(scenarioId: string): DraftMirror | null {
  try {
    const raw = window.localStorage.getItem(keyFor(scenarioId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftMirror>;
    // Shallow shape check only: this is our own data one page load ago, and the
    // model is re-validated by the schema on save regardless. What matters is
    // that a half-written or stale-format entry cannot crash the editor.
    if (
      typeof parsed?.version !== "number" ||
      typeof parsed.savedAt !== "string" ||
      typeof parsed.model !== "object" ||
      parsed.model === null
    ) {
      return null;
    }
    return parsed as DraftMirror;
  } catch {
    return null;
  }
}

export function writeDraftMirror(scenarioId: string, mirror: DraftMirror): void {
  try {
    window.localStorage.setItem(keyFor(scenarioId), JSON.stringify(mirror));
  } catch {
    // A full quota or a blocked store costs the recovery buffer, not the edit.
  }
}

export function clearDraftMirror(scenarioId: string): void {
  try {
    window.localStorage.removeItem(keyFor(scenarioId));
  } catch {
    // Nothing to do, and nothing worth telling the user.
  }
}
